/**
 * Asset Disposals
 * Authority: ASSET_DISPOSE | ASSET_MANAGE
 *
 * Endpoints:
 *   GET  /disposals?status&page&size
 *   POST /disposals/{id}/approve    { approved, comments?, rejectionReason? }
 *   POST /disposals/{id}/cancel     — ASSET_DISPOSE, PENDING only
 *   POST /disposals/{id}/complete   { disposalDate, disposalValue?, recipient?, referenceNumber?, notes? }
 */
import { useState, useCallback } from "react";
import { Trash2, Ban } from "lucide-react";
import { assetApi } from "../api";
import type { DisposalListParams } from "../api";
import type { DisposalApproveRequest, DisposalCompleteRequest } from "../types";
import { useAsyncData, useMutation, usePagination } from "../hooks";
import {
  ActiveFilterBadges, AssetError, AssetLoading, BackBtn, Btn,
  EmptyState, ExportBtn, FilterPanel, PageHeader, Pagination,
  SelectFilter, Table, TBody, TD, TH, THead, TR, fmt, fmtDate,
} from "../components/AssetUI";
import { downloadXls, DISPOSAL_EXPORT_HEADERS, disposalToRow } from "../utils/export";

const STATUS_OPTS = [
  { label: "Pending Review", value: "PENDING"   },
  { label: "Approved",       value: "APPROVED"  },
  { label: "Completed",      value: "COMPLETED" },
  { label: "Rejected",       value: "REJECTED"  },
  { label: "Cancelled",      value: "CANCELLED" },
];

const STATUS_CLS: Record<string, string> = {
  PENDING:   "bg-amber-100 text-amber-700",
  APPROVED:  "bg-emerald-100 text-emerald-700",
  COMPLETED: "bg-slate-100 text-slate-600",
  REJECTED:  "bg-red-100 text-red-600",
  CANCELLED: "bg-slate-200 text-slate-500",
};

type ActionRow =
  | { id: number; type: "approve";  assetName: string }
  | { id: number; type: "reject";   assetName: string }
  | { id: number; type: "complete"; assetName: string }
  | { id: number; type: "cancel";   assetName: string };

export default function AssetDisposalPage() {
  const pagination = usePagination();
  const [filters, setFilters] = useState<DisposalListParams>({ status: "PENDING" });
  const [action, setAction]   = useState<ActionRow | null>(null);

  // Approve / reject form state
  const [comments, setComments]         = useState("");
  const [rejectReason, setRejectReason] = useState("");

  // Complete form state
  const [completeForm, setCompleteForm] = useState({
    disposalDate: new Date().toISOString().slice(0,10),
    disposalValue: "", recipient: "", referenceNumber: "", notes: "",
  });

  const { data, loading, error, reload } = useAsyncData(
    () => assetApi.listDisposals(pagination.page, pagination.size, filters),
    [pagination.page, pagination.size, filters],
  );

  const approveOrReject = useMutation((payload: DisposalApproveRequest & { id: number }) => {
    const { id, ...rest } = payload;
    return assetApi.approveOrRejectDisposal(id, rest);
  });

  const complete = useMutation((payload: DisposalCompleteRequest & { id: number }) => {
    const { id, ...rest } = payload;
    return assetApi.completeDisposal(id, rest);
  });

  const cancel = useMutation((id: number) => assetApi.cancelDisposal(id));

  function setF<K extends keyof DisposalListParams>(k: K, v: DisposalListParams[K]) {
    setFilters((f) => ({ ...f, [k]: v })); pagination.reset();
  }

  function reset() {
    setAction(null);
    setComments(""); setRejectReason("");
    setCompleteForm({ disposalDate: new Date().toISOString().slice(0,10), disposalValue: "", recipient: "", referenceNumber: "", notes: "" });
    reload();
  }

  async function doApprove() {
    if (!action || action.type !== "approve") return;
    await approveOrReject.mutate({ id: action.id, approved: true, comments: comments || undefined });
    reset();
  }
  async function doReject() {
    if (!action || action.type !== "reject") return;
    await approveOrReject.mutate({ id: action.id, approved: false, comments: comments || undefined, rejectionReason: rejectReason });
    reset();
  }
  async function doComplete() {
    if (!action || action.type !== "complete") return;
    await complete.mutate({
      id: action.id,
      disposalDate:    completeForm.disposalDate,
      disposalValue:   completeForm.disposalValue ? Number(completeForm.disposalValue) : undefined,
      recipient:       completeForm.recipient       || undefined,
      referenceNumber: completeForm.referenceNumber || undefined,
      notes:           completeForm.notes           || undefined,
    });
    reset();
  }
  async function doCancel() {
    if (!action || action.type !== "cancel") return;
    await cancel.mutate(action.id);
    reset();
  }

  const activeBadges = [
    filters.status && { label: `Status: ${filters.status}`, onRemove: () => setF("status", "") },
  ].filter(Boolean) as { label: string; onRemove: () => void }[];

  const doExport = useCallback(() => {
    downloadXls(
      `disposals-${new Date().toISOString().slice(0,10)}.xls`,
      "Asset Disposals",
      DISPOSAL_EXPORT_HEADERS,
      (data?.content ?? []).map(disposalToRow),
    );
  }, [data]);

  const isBusy = approveOrReject.loading || complete.loading || cancel.loading;

  return (
    <div className="space-y-5">
      <PageHeader
        breadcrumb="Asset Management"
        title="Disposal Requests"
        actions={
          <div className="flex gap-2">
            <BackBtn to="/assets" label="Dashboard" />
            <ExportBtn onExport={doExport} />
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <FilterPanel
          activeCount={activeBadges.length}
          onClear={() => { setFilters({ status: "PENDING" }); pagination.reset(); }}
          onRefresh={reload}
        >
          <SelectFilter
            label="All Statuses"
            value={filters.status ?? ""}
            onChange={(v) => setF("status", v)}
            options={STATUS_OPTS}
          />
        </FilterPanel>
        <ActiveFilterBadges filters={activeBadges} />
      </div>

      {loading && <AssetLoading label="Loading disposal requests…" />}
      {error   && <AssetError message={error} onRetry={reload} />}

      {!loading && data && (
        <>
          <div className="rounded-2xl border bg-white shadow-sm overflow-hidden">
            <Table>
              <THead>
                <TH>Asset</TH><TH>Reason</TH><TH>Method</TH>
                <TH>Requested By</TH><TH>Requested</TH>
                <TH>Approved By</TH><TH right>Value</TH>
                <TH>Status</TH><TH right>Actions</TH>
              </THead>
              <TBody>
                {data.content.length === 0 ? (
                  <tr><td colSpan={9}><EmptyState icon={Trash2} title="No disposal requests" description="No requests match the selected filter." /></td></tr>
                ) : data.content.map((d) => (
                  <>
                    <TR key={d.id} className={action?.id === d.id ? "bg-slate-50" : ""}>
                      <TD>
                        <div className="font-semibold text-slate-800">{d.assetName}</div>
                        {d.assetNumber && <div className="font-mono text-xs text-slate-400">{d.assetNumber}</div>}
                      </TD>
                      <TD muted><span className="line-clamp-2 max-w-[160px] text-xs">{d.reason}</span></TD>
                      <TD muted><span className="text-xs">{d.disposalMethod?.replace(/_/g," ") ?? "—"}</span></TD>
                      <TD muted>{d.requestedBy ?? "—"}</TD>
                      <TD muted>{fmtDate(d.requestedAt)}</TD>
                      <TD muted>{d.approvedBy ?? "—"}</TD>
                      <TD right muted>{d.disposalValue != null ? fmt(d.disposalValue) : "—"}</TD>
                      <TD>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_CLS[d.status] ?? "bg-slate-100 text-slate-600"}`}>
                          {d.status.replace(/_/g," ")}
                        </span>
                      </TD>
                      <TD right>
                        <div className="flex items-center justify-end gap-1.5 flex-wrap">
                          {d.status === "PENDING" && (
                            <>
                              <Btn variant="emerald" size="sm"
                                onClick={() => setAction({ id: d.id, type: "approve", assetName: d.assetName })}>
                                Approve
                              </Btn>
                              <Btn variant="dangerOutline" size="sm"
                                onClick={() => setAction({ id: d.id, type: "reject", assetName: d.assetName })}>
                                Reject
                              </Btn>
                              <Btn variant="ghost" size="sm" icon={Ban}
                                onClick={() => setAction({ id: d.id, type: "cancel", assetName: d.assetName })}>
                                Cancel
                              </Btn>
                            </>
                          )}
                          {d.status === "APPROVED" && (
                            <Btn variant="danger" size="sm"
                              onClick={() => setAction({ id: d.id, type: "complete", assetName: d.assetName })}>
                              Complete
                            </Btn>
                          )}
                        </div>
                      </TD>
                    </TR>

                    {/* ── Inline action panel ── */}
                    {action?.id === d.id && (
                      <tr className={
                        action.type === "reject"  ? "bg-red-50/70"
                      : action.type === "complete"? "bg-orange-50/70"
                      : action.type === "cancel"  ? "bg-slate-50"
                      : "bg-emerald-50/70"
                      }>
                        <td colSpan={9} className="px-6 py-4">

                          {/* Approve */}
                          {action.type === "approve" && (
                            <div className="flex flex-wrap items-end gap-3">
                              <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
                                Comments (optional)
                                <input
                                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 w-64"
                                  placeholder="Comments on this approval…"
                                  value={comments}
                                  onChange={(e) => setComments(e.target.value)}
                                />
                              </label>
                              {approveOrReject.error && <p className="text-xs text-red-600">{approveOrReject.error}</p>}
                              <div className="flex gap-2">
                                <Btn variant="emerald" loading={isBusy} loadingText="Approving…" onClick={doApprove}>Confirm Approve</Btn>
                                <Btn onClick={() => setAction(null)}>Cancel</Btn>
                              </div>
                            </div>
                          )}

                          {/* Reject */}
                          {action.type === "reject" && (
                            <div className="flex flex-wrap items-end gap-3">
                              <label className="flex-1 flex flex-col gap-1 text-xs font-semibold text-slate-600">
                                Rejection Reason *
                                <input
                                  required
                                  className="rounded-xl border border-red-200 px-3 py-2 text-sm outline-none focus:border-red-400 min-w-64"
                                  placeholder="Reason for rejection…"
                                  value={rejectReason}
                                  onChange={(e) => setRejectReason(e.target.value)}
                                />
                              </label>
                              <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
                                Comments (optional)
                                <input
                                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 w-52"
                                  placeholder="Additional comments…"
                                  value={comments}
                                  onChange={(e) => setComments(e.target.value)}
                                />
                              </label>
                              {approveOrReject.error && <p className="text-xs text-red-600">{approveOrReject.error}</p>}
                              <div className="flex gap-2">
                                <Btn variant="danger" loading={isBusy} disabled={!rejectReason.trim()} loadingText="Rejecting…" onClick={doReject}>Confirm Rejection</Btn>
                                <Btn onClick={() => setAction(null)}>Cancel</Btn>
                              </div>
                            </div>
                          )}

                          {/* Cancel */}
                          {action.type === "cancel" && (
                            <div className="flex items-center gap-4">
                              <p className="text-sm text-slate-700">
                                Cancel the disposal request for <strong>{d.assetName}</strong>? The asset status will revert.
                              </p>
                              {cancel.error && <p className="text-xs text-red-600">{cancel.error}</p>}
                              <div className="flex gap-2 ml-auto">
                                <Btn variant="dangerOutline" loading={isBusy} loadingText="Cancelling…" onClick={doCancel}>Yes, Cancel Request</Btn>
                                <Btn onClick={() => setAction(null)}>Keep</Btn>
                              </div>
                            </div>
                          )}

                          {/* Complete */}
                          {action.type === "complete" && (
                            <div className="space-y-3">
                              <p className="text-sm font-semibold text-orange-800">
                                ⚠ Completing disposal permanently deactivates <strong>{d.assetName}</strong>.
                              </p>
                              <div className="flex flex-wrap items-end gap-3">
                                <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
                                  Disposal Date *
                                  <input type="date"
                                    value={completeForm.disposalDate}
                                    onChange={(e) => setCompleteForm((f) => ({ ...f, disposalDate: e.target.value }))}
                                    className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-orange-400" />
                                </label>
                                <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
                                  Value Recovered (KES)
                                  <input type="number" min={0}
                                    value={completeForm.disposalValue}
                                    onChange={(e) => setCompleteForm((f) => ({ ...f, disposalValue: e.target.value }))}
                                    className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-orange-400 w-36" placeholder="0" />
                                </label>
                                <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
                                  Recipient
                                  <input type="text"
                                    value={completeForm.recipient}
                                    onChange={(e) => setCompleteForm((f) => ({ ...f, recipient: e.target.value }))}
                                    className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-orange-400 w-44" placeholder="e.g. Scrap dealer" />
                                </label>
                                <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
                                  Reference No.
                                  <input type="text"
                                    value={completeForm.referenceNumber}
                                    onChange={(e) => setCompleteForm((f) => ({ ...f, referenceNumber: e.target.value }))}
                                    className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-orange-400 w-36" />
                                </label>
                                <label className="flex-1 flex flex-col gap-1 text-xs font-semibold text-slate-600">
                                  Notes
                                  <input type="text"
                                    value={completeForm.notes}
                                    onChange={(e) => setCompleteForm((f) => ({ ...f, notes: e.target.value }))}
                                    className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-orange-400 min-w-48" />
                                </label>
                              </div>
                              {complete.error && <p className="text-xs text-red-600">{complete.error}</p>}
                              <div className="flex gap-2">
                                <Btn variant="danger" loading={isBusy} disabled={!completeForm.disposalDate} loadingText="Completing…" onClick={doComplete}>
                                  Complete Disposal
                                </Btn>
                                <Btn onClick={() => setAction(null)}>Cancel</Btn>
                              </div>
                            </div>
                          )}

                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </TBody>
            </Table>
          </div>
          <Pagination page={data} onPageChange={pagination.setPage} onSizeChange={pagination.setSize} />
        </>
      )}
    </div>
  );
}
